"use client";

import { Fragment, useEffect, useState } from "react";
import { FC } from "react";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import { ClockIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { useData } from "data/data-provider";
import moment from "moment";

export interface RentalCarTimesRangeInputProps {
  className?: string;
  fieldClassName?: string;
  hasButtonSubmit?: boolean;
  dropOffLocationType?: boolean;
}

const timeSlots = Array.from({ length: 12 }, (_, i) => {
  const hour = i === 0 ? "12" : String(i).padStart(1, "0");
  return [`${hour}:00`, `${hour}:30`];
}).flat();

const periodOptions = ["AM", "PM"];

const RentalCarTimesRangeInput: FC<RentalCarTimesRangeInputProps> = ({
  className = "",
  fieldClassName = "[ nc-hero-field-padding ]",
  hasButtonSubmit = true,

}) => {
  const {
    startDate,
    startHours,
    startMinutes,
    startPeriod,
    endDate,
    endHours,
    endMinutes,
    loadingAvailableFleet,
    endPeriod,
    setEndHours,
    setEndMinutes,
    setEndPeriod,
    setStartHours,
    setStartMinutes,
    setStartPeriod,
    pickupLocation,
    dropOffLocation,
    callAvailableFleetAPI,
    dropoffLocationType,
    partiallyUnavailableTimes
  } = useData();

  const [startTime, setStartTime] = useState(`${startHours}:${startMinutes}`);
  const [endTime, setEndTime] = useState(`${endHours}:${endMinutes}`);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    validateEndTime();
  }, [startDate]);

  useEffect(() => {
    if (startDate) {
      if (location.pathname === "/car-listing-detail") {
        const { hour, minute, period, timeStr } = findNextAvailableTime(
          startDate,
          true,
          10,
          0,
          "AM"
        );
        setStartHours(hour);
        setStartMinutes(minute);
        setStartPeriod(period);
        setStartTime(timeStr);
        setTimeout(() => {
          const isTimeUnavail = isTimeUnavailable(timeStr, period, true);
          if (isTimeUnavail) {
            console.warn(
              "Selected start time is unavailable, finding alternative..."
            );
            const availableSlot = findFirstAvailableTimeSlot(startDate, true);
            if (availableSlot) {
              setStartTime(availableSlot.timeStr);
              setStartHours(availableSlot.hour);
              setStartMinutes(availableSlot.minute);
              setStartPeriod(availableSlot.period);
            }
          }
        }, 0);
      } else {
        setStartHours("10");
        setStartMinutes("00");
        setStartPeriod("AM");
        setStartTime("10:00");
      }
    }
  }, [startDate, partiallyUnavailableTimes, location.pathname]);

  useEffect(() => {
    if (endDate) {
      if (location.pathname === "/car-listing-detail") {
        const { hour, minute, period, timeStr } = findNextAvailableTime(
          endDate,
          false,
          5,
          0,
          "PM"
        );

        setEndHours(hour);
        setEndMinutes(minute);
        setEndPeriod(period);
        setEndTime(timeStr);

        setTimeout(() => {
          const isTimeUnavail = isTimeUnavailable(timeStr, period, false);
          if (isTimeUnavail) {
            console.warn(
              "Selected end time is unavailable, finding alternative..."
            );
            const availableSlot = findFirstAvailableTimeSlot(endDate, false);
            if (availableSlot) {
              setEndTime(availableSlot.timeStr);
              setEndHours(availableSlot.hour);
              setEndMinutes(availableSlot.minute);
              setEndPeriod(availableSlot.period);
            }
          }
        }, 0);
      } else {
        setEndHours("5");
        setEndMinutes("00");
        setEndPeriod("PM");
        setEndTime("5:00");
      }
    }
  }, [endDate, partiallyUnavailableTimes, location.pathname]);

  const validateEndTime = () => {
    if (
      startDate &&
      endDate &&
      startDate.toDateString() === endDate.toDateString()
    ) {
      const [startHour, startMinute] = startTime.split(":").map(Number);
      const [endHour, endMinute] = endTime.split(":").map(Number);

      const startTimeObj = new Date();
      startTimeObj.setHours(
        startHour + (startPeriod === "PM" && startHour !== 12 ? 12 : 0),
        startMinute
      );

      const endTimeObj = new Date();
      endTimeObj.setHours(
        endHour + (endPeriod === "PM" && endHour !== 12 ? 12 : 0),
        endMinute
      );
    }
  };

  const isTimeUnavailable = (
    time: string,
    period: string,
    isStartTime: boolean
  ) => {
    if (location.pathname !== "/car-listing-detail") {
      return false;
    }

    const dateToCheck = isStartTime ? startDate : endDate;
    if (!dateToCheck) return false;

    const dateKey = moment(dateToCheck).format("YYYY-MM-DD");
    const [hourStr, minuteStr] = time.split(":");
    let hour = parseInt(hourStr, 10);
    if (period === "PM" && hour !== 12) hour += 12;
    if (period === "AM" && hour === 12) hour = 0;
    const timeStr = `${hour.toString().padStart(2, "0")}:${minuteStr}:00`;

    if (
      startDate &&
      endDate &&
      startDate.toDateString() !== endDate.toDateString()
    ) {
      const partialInfoForDate = partiallyUnavailableTimes?.[dateKey];
      if (!partialInfoForDate) return false;
      if (Array.isArray(partialInfoForDate)) {
        for (const range of partialInfoForDate) {
          if (
            range.from &&
            timeStr >= range.from &&
            range.to &&
            timeStr <= range.to
          ) {
            return true;
          }
        }
      } else if (
        partialInfoForDate.from &&
        timeStr >= partialInfoForDate.from &&
        partialInfoForDate.to &&
        timeStr <= partialInfoForDate.to
      ) {
        return true;
      }
      return false;
    }
    const partialInfo = partiallyUnavailableTimes?.[dateKey];
    if (!partialInfo) return false;
    if (Array.isArray(partialInfo)) {
      for (const range of partialInfo) {
        if (
          range.from &&
          timeStr >= range.from &&
          range.to &&
          timeStr <= range.to
        ) {
          return true;
        }
      }
      return false;
    } else if (
      partialInfo.from &&
      timeStr >= partialInfo.from &&
      partialInfo.to &&
      timeStr <= partialInfo.to
    ) {
      return true;
    }

    return false;
  };

  const renderInput = () => {
    return (
      <>
        <div className="text-neutral-300 dark:text-neutral-400">
          <ClockIcon className="w-5 h-5 lg:w-7 lg:h-7" />
        </div>
        <div className="flex-grow text-left">
          <span className="block text-lg font-semibold">
            {`${startTime} ${startPeriod}`} - {`${endTime} ${endPeriod}`}
          </span>
          <span className="block mt-1 text-sm text-neutral-400 leading-none font-light">
            {"Pick up - Drop off"}
          </span>
        </div>
      </>
    );
  };

  const isWithinOperationalHours = (
    startHours: string,
    startMinutes: string,
    startPeriod: string,
    endHours: string,
    endMinutes: string,
    endPeriod: string,
    ourCompany: any
  ): boolean => {
    if (ourCompany?.always_operational) {
      return true;
    }

    const convertTo24HourFormat = (hour: number, period: string) => {
      return hour + (period === "PM" && hour !== 12 ? 12 : 0);
    };
    const [opStartHour, opStartMinute] = ourCompany?.operational_start_time
      .split(":")
      .map(Number);
    const [opEndHour, opEndMinute] = ourCompany?.operational_end_time
      .split(":")
      .map(Number);

    const operationalStart = new Date();
    operationalStart.setHours(opStartHour, opStartMinute, 0, 0);

    const operationalEnd = new Date();
    operationalEnd.setHours(opEndHour, opEndMinute, 0, 0);
    const startHour24 = convertTo24HourFormat(Number(startHours), startPeriod);
    const selectedStart = new Date();
    selectedStart.setHours(startHour24, Number(startMinutes), 0, 0);
    const endHour24 = convertTo24HourFormat(Number(endHours), endPeriod);
    const selectedEnd = new Date();
    selectedEnd.setHours(endHour24, Number(endMinutes), 0, 0);
    const isStartWithinHours =
      selectedStart >= operationalStart && selectedStart <= operationalEnd;
    const isEndWithinHours =
      selectedEnd >= operationalStart && selectedEnd <= operationalEnd;

    return isStartWithinHours && isEndWithinHours;
  };

  useEffect(() => {
    if (startDate) {
      const dateKey = moment(startDate).format("YYYY-MM-DD");
    }
  }, [startDate, partiallyUnavailableTimes]);

  const findNextAvailableTime = (
    date: Date | null,
    isStartTime: boolean,
    defaultHour: number,
    defaultMinute: number,
    defaultPeriod: string
  ): { hour: string; minute: string; period: string; timeStr: string } => {
    if (!date) {
      return getDefaultTimeObject(defaultHour, defaultMinute, defaultPeriod);
    }

    const dateKey = moment(date).format("YYYY-MM-DD");
    const partialInfo = partiallyUnavailableTimes?.[dateKey];

    if (!partialInfo) {
      return getDefaultTimeObject(defaultHour, defaultMinute, defaultPeriod);
    }

    let hour24 = defaultHour;
    if (defaultPeriod === "PM" && defaultHour !== 12) hour24 += 12;
    if (defaultPeriod === "AM" && defaultHour === 12) hour24 = 0;

    const defaultTimeStr = `${hour24
      .toString()
      .padStart(2, "0")}:${defaultMinute.toString().padStart(2, "0")}:00`;
    const firstRange = Array.isArray(partialInfo)
      ? partialInfo[0]
      : partialInfo;

    if (isStartTime) {
      if (firstRange.from && defaultTimeStr >= firstRange.from) {
        const [fromHours, fromMinutes] = firstRange.from.split(":");
        let fromHour = parseInt(fromHours);
        const fromMinute = parseInt(fromMinutes);
        for (let h = 9; h < fromHour; h++) {
          const newPeriod = h >= 12 ? "PM" : "AM";
          let newHour = h % 12;
          if (newHour === 0) newHour = 12;

          return {
            hour: newHour.toString(),
            minute: "00",
            period: newPeriod,
            timeStr: `${newHour}:00`,
          };
        }
        return {
          hour: "9",
          minute: "00",
          period: "AM",
          timeStr: "9:00",
        };
      }
    } else {
      if (firstRange.to && defaultTimeStr <= firstRange.to) {
        const [toHours, toMinutes] = firstRange.to.split(":");
        let toHour = parseInt(toHours);
        toHour += 1;

        if (toHour > 23) toHour = 23;
        const newPeriod = toHour >= 12 ? "PM" : "AM";
        let newHour = toHour % 12;
        if (newHour === 0) newHour = 12;

        return {
          hour: newHour.toString(),
          minute: "00",
          period: newPeriod,
          timeStr: `${newHour}:00`,
        };
      }
    }

    return getDefaultTimeObject(defaultHour, defaultMinute, defaultPeriod);
  };
  const getDefaultTimeObject = (
    hour: number,
    minute: number,
    period: string
  ) => {
    return {
      hour: hour.toString(),
      minute: minute.toString().padStart(2, "0"),
      period,
      timeStr: `${hour}:${minute.toString().padStart(2, "0")}`,
    };
  };
  const findFirstAvailableTimeSlot = (
    date: Date | null,
    isStartTime: boolean
  ) => {
    if (!date) return null;
    for (const period of ["AM", "PM"]) {
      for (const time of timeSlots) {
        if (!isTimeUnavailable(time, period, isStartTime)) {
          const [hour, minute] = time.split(":");
          return {
            hour,
            minute,
            period,
            timeStr: time,
          };
        }
      }
    }

    return null;
  };

  return (
    <>
      <Popover
        className={`RentalCarDatesRangeInput relative flex ${className}`}
      >
        {({ open }) => (
          <>
            <div
              className={`flex-1 z-10 flex items-center focus:outline-none ${open ? "nc-hero-field-focused rounded-full" : ""
              }`}
            >
              <PopoverButton
                className={`flex-1 z-10 flex relative p-4 [ nc-hero-field-padding ] items-center space-x-3 focus:outline-none `}
                onClickCapture={() => document.querySelector("html")?.click()}
              >
                {renderInput()}
              </PopoverButton>

              {/* BUTTON SUBMIT OF FORM */}

              {location.pathname !== "/car-listing-detail" && (

                <div className="hidden lg:block pr-2 xl:pr-4 cursor-pointer">

                  <div className="pr-2 xl:pr-4">
                    <div
                      onClick={() => {
                        // if (
                        //   !isWithinOperationalHours(
                        //     startHours,
                        //     startMinutes,
                        //     startPeriod,
                        //     endHours,
                        //     endMinutes,
                        //     endPeriod,
                        //     ourCompany
                        //   )
                        // ) {
                        //   toast.error(
                        //     `Please select a pickup and drop-off time between our working hours of ${moment(
                        //       ourCompany?.operational_start_time,
                        //       "HH:mm:ss"
                        //     ).format("hh:mm A")} - ${moment(
                        //       ourCompany?.operational_end_time,
                        //       "HH:mm:ss"
                        //     ).format("hh:mm A")}`
                        //   );
                        //   return;
                        // }

                        if (pickupLocation === null) {
                          toast.error("Please enter Pick up location");
                        } else if (
                          dropOffLocation === null &&
                        dropoffLocationType === false
                        ) {
                          toast.error("Please enter Drop off location");
                        } else if (startDate === null && endDate === null) {
                          toast.error("Please enter start and end date");
                        } else if (
                          pickupLocation !== null
                        ) {
                          callAvailableFleetAPI(navigate);
                        }
                      }}
                      className={`h-14 md:h-16 w-full md:w-16 rounded-full bg-primary-200 hover:bg-primary-700 flex items-center justify-center text-neutral-50 focus:outline-none `}
                    >
                      {loadingAvailableFleet ? (
                        <svg
                          aria-hidden="true"
                          className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-400"
                          viewBox="0 0 100 101"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                            fill="currentColor"
                          />
                          <path
                            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                            fill="currentFill"
                          />
                        </svg>
                      ) : (
                        <>
                          <span className="mr-3 md:hidden">Search</span>
                          <svg
                            width="26"
                            height="26"
                            viewBox="0 0 26 26"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M12.4582 22.7503C18.1421 22.7503 22.7498 18.1426 22.7498 12.4587C22.7498 6.77473 18.1421 2.16699 12.4582 2.16699C6.77424 2.16699 2.1665 6.77473 2.1665 12.4587C2.1665 18.1426 6.77424 22.7503 12.4582 22.7503Z"
                              stroke="white"
                              stroke-width="1.5"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                            <path
                              d="M23.8332 23.8337L21.6665 21.667"
                              stroke="white"
                              stroke-width="1.5"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                        </>
                      )}
                    </div>
                  </div>

                </div>
              )}
            </div>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <PopoverPanel className="absolute left-1/2 z-10 mt-3 top-full w-screen max-w-sm -translate-x-1/2 transform px-4 sm:px-0 lg:max-w-l">
                <div className="p-4 space-x-2 overflow-hidden rounded-3xl shadow-lg ring-1 ring-black ring-opacity-5 bg-white dark:bg-[#140B0B]">
                  <div className="grid grid-cols-2">
                    <div>
                      <h3 className="text-lg font-medium flex justify-center">
                        Start Time
                      </h3>
                      <div className="grid grid-cols-2">
                        <div className="flex flex-wrap overflow-y-auto h-48 hide-scrollbar">
                          {timeSlots.map((time) => {
                            const isUnavailable =
                              startDate &&
                              isTimeUnavailable(time, startPeriod, true);
                            return (
                              <div
                                key={time}
                                className={`cursor-pointer py-2 px-4 hover:bg-primary-200 hover:text-white rounded-full ${
                                  startTime === time
                                    ? "bg-primary-200 text-white"
                                    : ""
                                } ${
                                  isUnavailable
                                    ? "opacity-50 cursor-not-allowed line-through"
                                    : ""
                                }`}
                                onClick={() => {
                                  if (
                                    location.pathname ===
                                      "/car-listing-detail" &&
                                    isUnavailable
                                  ) {
                                    toast.error(
                                      "This time slot is unavailable due to a partial booking"
                                    );
                                  } else {
                                    setStartTime(time);
                                    const [hour, minutes] = time.split(":");
                                    setStartHours(hour);
                                    setStartMinutes(minutes);
                                  }
                                }}
                              >
                                {time}
                              </div>
                            );
                          })}
                        </div>

                        <div className="flex flex-col max-w-fit overflow-y-auto h-48 hide-scrollbar">
                          {periodOptions.map((periodOption) => {
                            // Check if all time slots for this period are unavailable
                            const allSlotsUnavailable =
                              startDate &&
                              timeSlots.every((time) =>
                                isTimeUnavailable(time, periodOption, true)
                              );

                            return (
                              <div
                                key={periodOption}
                                className={`py-2 px-4 rounded-full ${
                                  startPeriod === periodOption
                                    ? "bg-primary-200 text-white"
                                    : ""
                                } ${
                                  allSlotsUnavailable
                                    ? "opacity-50 cursor-not-allowed line-through"
                                    : "cursor-pointer hover:bg-primary-200 hover:text-white"
                                }`}
                                onClick={() => {
                                  if (!allSlotsUnavailable) {
                                    setStartPeriod(periodOption);
                                  } else {
                                    toast.error(
                                      `All ${periodOption} time slots are unavailable for the selected date.`
                                    );
                                  }
                                }}
                              >
                                {periodOption}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-medium flex justify-center">
                        End Time
                      </h3>
                      <div className="grid grid-cols-2">
                        <div className="flex flex-wrap overflow-y-auto h-48 hide-scrollbar">
                          {timeSlots.map((time) => {
                            const isUnavailable =
                              endDate &&
                              isTimeUnavailable(time, endPeriod, false);
                            return (
                              <div
                                key={time}
                                className={`cursor-pointer py-2 px-4 hover:bg-primary-200 hover:text-white rounded-full ${
                                  endTime === time
                                    ? "bg-primary-200 text-white"
                                    : ""
                                } ${
                                  isUnavailable
                                    ? "opacity-50 cursor-not-allowed line-through"
                                    : ""
                                }`}
                                onClick={() => {
                                  if (
                                    location.pathname ===
                                      "/car-listing-detail" &&
                                    isUnavailable
                                  ) {
                                    toast.error(
                                      "This time slot is unavailable due to a partial booking"
                                    );
                                  } else {
                                    setEndTime(time);
                                    const [hour, minutes] = time.split(":");
                                    setEndHours(hour);
                                    setEndMinutes(minutes);
                                  }
                                }}
                              >
                                {time}
                              </div>
                            );
                          })}
                        </div>

                        <div className="flex flex-col max-w-fit overflow-y-auto h-48 hide-scrollbar">
                          {periodOptions.map((periodOption) => {
                            // Check if all time slots for this period are unavailable
                            const allSlotsUnavailable =
                              endDate &&
                              timeSlots.every((time) =>
                                isTimeUnavailable(time, periodOption, false)
                              );

                            return (
                              <div
                                key={periodOption}
                                className={`py-2 px-4 rounded-full ${
                                  endPeriod === periodOption
                                    ? "bg-primary-200 text-white"
                                    : ""
                                } ${
                                  allSlotsUnavailable
                                    ? "opacity-50 cursor-not-allowed line-through"
                                    : "cursor-pointer hover:bg-primary-200 hover:text-white"
                                }`}
                                onClick={() => {
                                  if (!allSlotsUnavailable) {
                                    setEndPeriod(periodOption);
                                  } else {
                                    toast.error(
                                      `All ${periodOption} time slots are unavailable for the selected date.`
                                    );
                                  }
                                }}
                              >
                                {periodOption}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </PopoverPanel>
            </Transition>
          </>
        )}
      </Popover>
      {location.pathname !== "/car-listing-detail" && (
        <div className="lg:hidden cursor-pointer mb-2">
          <div className="flex justify-center">
            <div
              onClick={() => {
                if (pickupLocation === null) {
                  toast.error("Please enter Pick up location");
                } else if (
                  dropOffLocation === null &&
                   dropoffLocationType === false
                ) {
                  toast.error("Please enter Drop off location");
                } else if (startDate === null && endDate === null) {
                  toast.error("Please enter start and end date");
                } else if (pickupLocation !== null) {
                  callAvailableFleetAPI(navigate);
                }
              }}
              className={`h-14 md:h-16 w-full rounded-full bg-primary-200 hover:bg-primary-700 flex items-center justify-center text-neutral-50 focus:outline-none ${startDate && endDate ? "" : ""
              }`}
            >
              {loadingAvailableFleet ? (
                <svg
                  aria-hidden="true"
                  className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-400"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  />
                </svg>
              ) : (
                <>
                  <span className="text-xl font-bold">
                    Search Available Cars
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RentalCarTimesRangeInput;
